import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Container, Link, Paper, TextField, Typography } from '@mui/material';
import api from '../../services/api';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err: any) {
      // Show the generic success message even on errors so the form
      // cannot be used to discover which emails have accounts.
      if (err?.response?.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setSent(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <Paper sx={{ p: 4, width: '100%' }}>
          <Typography variant="h5" gutterBottom>Forgot your password?</Typography>
          {sent ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                If an account exists for {email}, a password reset link has been sent.
                Please check your inbox (and spam folder).
              </Alert>
              <Typography variant="body2" align="center">
                <Link component={RouterLink} to="/login">Back to sign in</Link>
              </Typography>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the email address for your account and we will send you a reset link.
              </Typography>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <TextField label="Email Address" type="email" value={email}
                onChange={e => setEmail(e.target.value)} fullWidth required sx={{ mb: 2 }} />
              <Button type="submit" variant="contained" fullWidth disabled={submitting}>
                {submitting ? 'Sending...' : 'Send reset link'}
              </Button>
              <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                <Link component={RouterLink} to="/login">Back to sign in</Link>
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;
